import {
    createContext,
    type FC,
    type PropsWithChildren,
    useCallback,
    useContext,
    useRef,
    useState,
} from "react";
import { fetchItemListPrices } from "../utils/fetchItemListPrices";

type PriceOverrideContextType = {
    getPriceData: (hit: { itemId: string | number; priceData: PriceData }) => PriceData;
    requestPrices: (items: { itemNo?: string; itemId: string | number }[]) => void;
};

const PriceOverrideContext = createContext<PriceOverrideContextType | undefined>(undefined);

export const PriceOverrideProvider: FC<PropsWithChildren> = ({ children }) => {
    const [overrides, setOverrides] = useState<Record<string, PriceData>>({});
    const requestedIds = useRef<Set<string>>(new Set());
    const pending = useRef<Promise<void> | null>(null);

    const requestPrices = useCallback(
        (items: { itemNo?: string; itemId: string | number }[]) => {
            const missing = items.filter((i) => !requestedIds.current.has(String(i.itemId)));
            if (!missing.length) return;

            missing.forEach((i) => requestedIds.current.add(String(i.itemId)));

            const run = async () => {
                const result = await fetchItemListPrices(missing);
                if (Object.keys(result).length) {
                    setOverrides((prev) => ({ ...prev, ...result }));
                }
            };

            // Chain requests so a fast filter change doesn't fire overlapping fetches.
            pending.current = (pending.current ?? Promise.resolve()).then(run);
        },
        []
    );

    const getPriceData = useCallback(
        (hit: { itemId: string | number; priceData: PriceData }): PriceData => {
            return overrides[String(hit.itemId)] ?? hit.priceData;
        },
        [overrides]
    );

    return (
        <PriceOverrideContext.Provider value={{ getPriceData, requestPrices }}>
            {children}
        </PriceOverrideContext.Provider>
    );
};

export const usePriceOverrideContext = () => {
    const context = useContext(PriceOverrideContext);
    if (!context) throw new Error("usePriceOverrideContext must be used within a PriceOverrideProvider");
    return context;
};
